import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';
import { Loader2 } from 'lucide-react';
import { BACKEND_URL } from '@/utils/backendUrl';
import { saveSessionToken } from '@/utils/api';

const AuthCallback = () => {
  const navigate = useNavigate();
  const hasProcessed = useRef(false);

  useEffect(() => {
    // CRITICAL: Prevent double processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processCallback = async () => {
      // Extract the OAuth result from the URL fragment. Two shapes:
      //  - legacy Emergent broker: #session_id=… (exchanged via /google-session)
      //  - direct Google OAuth:    #g_token=… | #g_pending=… | #g_error=…
      //    (the backend already completed the exchange — nothing to trade in)
      const hash = window.location.hash;
      const params = new URLSearchParams(hash.substring(1));
      const sessionId = params.get('session_id');
      const gToken = params.get('g_token');
      const gPending = params.get('g_pending');
      const gError = params.get('g_error');

      if (gError) {
        toast.error(
          gError === 'cancelled'
            ? 'Google sign-in was cancelled.'
            : 'Google sign-in failed. Please try again or use your email and password.'
        );
        navigate('/login', { replace: true });
        return;
      }

      // ── Direct flow: new Google user — show Terms first ──
      if (gPending) {
        navigate('/accept-terms', {
          state: {
            pending_token: gPending,
            user_info: {
              email: params.get('g_email') || '',
              full_name: params.get('g_name') || '',
              picture: params.get('g_picture') || null,
            },
          },
          replace: true,
        });
        return;
      }

      if (!sessionId && !gToken) {
        toast.error('Invalid authentication callback');
        navigate('/login');
        return;
      }

      try {
        let user;
        if (gToken) {
          // ── Direct flow: session already created — fetch the user with it ──
          saveSessionToken({ token: gToken });
          const meRes = await axios.get(`${BACKEND_URL}/api/auth/me`, {
            headers: { Authorization: `Bearer ${gToken}` },
            withCredentials: true,
          });
          user = meRes.data;
        } else {
          const response = await axios.post(
            `${BACKEND_URL}/api/auth/google-session`,
            null,
            {
              params: { session_id: sessionId },
              withCredentials: true
            }
          );

          // ── New user — account NOT created yet, show Terms first ──
          if (response.data.status === 'pending_terms') {
            navigate('/accept-terms', {
              state: {
                pending_token: response.data.pending_token,
                user_info: response.data.user_info,
              },
              replace: true,
            });
            return;
          }

          user = response.data.user;
          saveSessionToken(response.data);
        }

        // Determine effective journey type — DB value is authoritative,
        // localStorage is fallback for Google OAuth users whose DB defaults to 'matchmaking'
        const coupleVertical = localStorage.getItem('ss_vertical') === 'couple';
        localStorage.removeItem('ss_vertical');
        const isInsights =
          coupleVertical ||
          user.journey_type === 'insights' ||
          localStorage.getItem('ss_journey_type') === 'insights';
        if (isInsights) localStorage.setItem('ss_journey_type', 'insights');
        const homeDest = isInsights ? '/insights/dashboard' : '/dashboard';

        // ── Existing user with outdated/missing terms ──
        if (!user.terms_accepted) {
          navigate('/accept-terms', {
            state: {
              returnTo: user.is_profile_complete ? homeDest : '/onboarding/profile',
            },
            replace: true,
          });
          return;
        }

        // ── Normal flow ──
        // Restore any pre-OAuth returnTo destination (e.g. user was going to /subscription)
        const savedReturnTo = localStorage.getItem('ss_return_to');
        localStorage.removeItem('ss_return_to');

        if (!user.is_profile_complete) {
          navigate('/onboarding/profile', { state: { user }, replace: true });
        } else if (savedReturnTo) {
          navigate(savedReturnTo, { replace: true });
        } else if (isInsights) {
          navigate('/insights/dashboard', { state: { user }, replace: true });
        } else {
          navigate('/dashboard', { state: { user }, replace: true });
        }
      } catch (error) {
        console.error('Auth callback error:', error);
        toast.error('Authentication failed. Please try again.');
        navigate('/login', { replace: true });
      }
    };

    processCallback();
  }, [navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background to-card">
      <div className="text-center">
        <Loader2 className="w-12 h-12 animate-spin text-primary mx-auto mb-4" />
        <p className="text-lg text-muted-foreground">Completing authentication...</p>
      </div>
    </div>
  );
};

export default AuthCallback;
